"use client";

import React, { useState } from "react";
import { Button } from "../common/button";

export interface TherapistTiersAlternativeProps {
  title?: string;
  subtitle?: string;
  onCtaClick?: () => void;
}

export function TherapistTiersAlternative({
  title = "Find Your Perfect Therapist Match",
  subtitle = "Two paths to healing, both designed with you in mind",
  onCtaClick
}: TherapistTiersAlternativeProps) {
  const [selectedTier, setSelectedTier] = useState<'resident' | 'associate' | null>(null);
  const [showResidentScope, setShowResidentScope] = useState(false);

  const tiers = {
    resident: {
      title: "Intern Therapists",
      tagline: "Rising stars in mental health",
      price: "$45-$105",
      description: "Think of medical residents at top hospitals - that's our model. These are exceptional clinicians in their final training phase.",
      headline: "Why clients love our interns:",
      keyPoints: [
        {
          icon: "🎓",
          title: "Elite training",
          detail: "Carefully selected from top graduate programs"
        },
        {
          icon: "👥",
          title: "Double the support",
          detail: "You get your therapist PLUS their supervisor's expertise"
        },
        {
          icon: "💡",
          title: "Current training",
          detail: "Learning evidence-based approaches in their programs"
        },
        {
          icon: "🌟",
          title: "Life experience",
          detail: "Many bring wisdom from previous careers and life journeys"
        }
      ],
      proof: "Receive ongoing supervision and case consultation",
      guarantee: "Not the right fit? Switch therapists anytime, no questions asked",
      scopeInfo: {
        canHelp: [
          "Anxiety, worry, and stress",
          "Depression and low mood",
          "Life transitions and adjustments",
          "Relationship challenges",
          "Self-esteem and confidence",
          "Grief and loss",
          "Work/school stress",
          "Family dynamics",
          "Identity exploration",
          "Sleep difficulties",
          "General mental wellness"
        ],
        referToAssociate: [
          "Active substance abuse requiring specialized treatment",
          "Recent suicide attempts or active self-harm",
          "Severe eating disorders requiring medical monitoring",
          "Psychosis or severe mental illness",
          "Court-mandated therapy",
          "Specialized trauma therapy (EMDR, CPT)",
          "Personality disorders requiring DBT",
          "Couples or family therapy (some residents do offer this)",
          "Child/adolescent therapy (under 18)"
        ]
      }
    },
    associate: {
      title: "Associate Therapists",
      tagline: "Proven expertise",
      price: "$115-$175",
      description: "Seasoned professionals with the experience to handle life's complexities. Licensed or on the path to licensure.",
      headline: "When experience matters most:",
      keyPoints: [
        {
          icon: "🏆",
          title: "Real experience",
          detail: "Have worked with diverse clients and situations"
        },
        {
          icon: "🔍",
          title: "Pattern recognition",
          detail: "Quickly identify what's really going on beneath the surface"
        },
        {
          icon: "🛠️",
          title: "Diverse toolkit",
          detail: "Multiple certifications and approaches to fit your needs"
        },
        {
          icon: "🎯",
          title: "Refined intuition",
          detail: "Know exactly when to push and when to hold space"
        }
      ],
      proof: "Completed advanced clinical training beyond their degree",
      guarantee: "Not the right fit? Switch therapists anytime"
    }
  };

  return (
    <section className="py-20 px-4 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {title}
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {subtitle}
          </p>
        </div>

        {/* Interactive Comparison */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {(['resident', 'associate'] as const).map((tierKey) => {
            const tier = tiers[tierKey];
            const isSelected = selectedTier === tierKey;
            
            return (
              <div
                key={tierKey}
                className={`relative cursor-pointer transition-all duration-300 ${
                  isSelected ? 'scale-105' : 'hover:scale-102'
                }`}
                onClick={() => setSelectedTier(tierKey)}
              >
                <div className={`bg-white rounded-3xl p-8 h-full border-2 transition-all ${
                  isSelected 
                    ? 'border-primary-500 shadow-xl' 
                    : 'border-gray-100 shadow-md hover:shadow-lg'
                }`}>
                  {/* Price badge */}
                  <div className="absolute -top-4 left-8 bg-primary-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                    {tier.price} per session
                  </div>

                  {/* Header */}
                  <div className="mb-6 pt-2">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {tier.title}
                    </h3>
                    <p className="text-primary-600 font-medium">
                      {tier.tagline}
                    </p>
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 mb-8 leading-relaxed">
                    {tier.description}
                  </p>

                  {/* Headline */}
                  <p className="font-medium text-gray-900 mb-4">
                    {tier.headline}
                  </p>

                  {/* Key Points */}
                  <div className="space-y-4 mb-6">
                    {tier.keyPoints.map((point, idx) => (
                      <div key={idx} className="flex gap-3">
                        <span className="text-2xl flex-shrink-0">{point.icon}</span>
                        <div>
                          <p className="font-medium text-gray-900">{point.title}</p>
                          <p className="text-sm text-gray-600">{point.detail}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Proof and Guarantee */}
                  <div className="space-y-3 pt-4 border-t border-gray-100">
                    <div className="flex items-start gap-2">
                      <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-sm text-gray-700">{tier.proof}</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <svg className="w-5 h-5 text-primary-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      <span className="text-sm font-medium text-gray-700">{tier.guarantee}</span>
                    </div>
                  </div>

                  {/* Scope info for residents - always visible */}
                  {tier === tiers.resident && (
                    <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-amber-900 mb-1">
                            Scope limitations apply
                          </p>
                          <p className="text-xs text-amber-700 mb-2">
                            Residents cannot treat severe conditions like active addiction, recent suicide attempts, or court-mandated therapy.
                          </p>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowResidentScope(!showResidentScope);
                            }}
                            className="text-xs text-amber-700 hover:text-amber-800 font-medium underline"
                          >
                            {showResidentScope ? 'Hide' : 'See'} full list
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Expanded scope details - shows within the card */}
                  {tier === tiers.resident && showResidentScope && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <div className="grid gap-4">
                        <div>
                          <p className="text-xs font-semibold text-green-700 mb-2">✓ Can help with:</p>
                          <p className="text-xs text-gray-600">
                            Anxiety, depression, stress, relationships, life transitions, grief, work/school issues, self-esteem, identity, sleep, general wellness
                          </p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-red-700 mb-2">✗ Cannot treat:</p>
                          <p className="text-xs text-gray-600">
                            Active substance abuse, recent suicide attempts, self-harm, severe eating disorders, psychosis, court-mandated therapy, specialized trauma therapy (EMDR/CPT), personality disorders needing DBT, most couples/family therapy, children under 18
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Selection indicator */}
                  {isSelected && (
                    <div className="absolute top-6 right-6">
                      <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Help text */}
        <div className="text-center mb-12">
          <p className="text-gray-600">
            <span className="font-medium">Not sure which to choose?</span> Our matching quiz will help you decide based on your needs and budget.
          </p>
        </div>

        {/* FAQs */}
        <div className="bg-white rounded-2xl p-8 mb-12">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Common Questions</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Can I switch between tiers?</h4>
              <p className="text-sm text-gray-600">
                Yes! You can change your therapist or tier anytime. Your mental health needs may evolve, and we're here to support that.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Are Resident therapists qualified?</h4>
              <p className="text-sm text-gray-600">
                Absolutely. They're completing Master's degrees and receive regular supervision. Many clients find them incredibly helpful.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">How do I know my rate?</h4>
              <p className="text-sm text-gray-600">
                You choose any rate within the range that works for your budget. Your therapist never knows what you pay.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">What if I can't afford Associate rates?</h4>
              <p className="text-sm text-gray-600">
                Start with a Resident therapist! They're excellent, and you can always transition later if needed.
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button 
            size="lg"
            className="bg-primary-600 hover:bg-primary-700"
            onClick={onCtaClick}
          >
            Take Our Matching Quiz
          </Button>
          <p className="text-sm text-gray-500 mt-4">
            2-minute quiz • No commitment • Free to browse
          </p>
        </div>
      </div>
    </section>
  );
}